import { Link, useNavigate } from "react-router-dom";
import { LuMenu } from "react-icons/lu";
import apiClient from "../api-services/ApiClient";
import { getUser, removeToken, removeUser } from "../api-services/Process";
import { SITELINKS } from "../app-routes/links";
import HoneConnectLogoHeader1 from "../assets/img/HoonConnectLogoHeader1.png";
import NotificationBell from "./header/NotificationBell";
import ProfileMenu from "./header/ProfileMenu";

type HeaderAdminProps = {
  /** shifts the header so it starts where the sidebar ends */
  sidebarCollapsed?: boolean;
  /** phones only: opens the sidebar drawer */
  onMenuClick?: () => void;
  menuOpen?: boolean;
};

const HeaderAdmin = ({
  sidebarCollapsed = false,
  onMenuClick,
  menuOpen = false,
}: HeaderAdminProps) => {
  const navigate = useNavigate();
  const user = getUser();

  // The admin record's picture may be a relative storage path or an absolute URL
  // (Avatar handles both). The field name depends on the login response.
  const photo: string | null =
    user?.profile_pix_link ?? user?.profile_photo ?? user?.avatar ?? user?.image ?? null;

  const handleLogout = async () => {
    // End the session on the server first, so the token stops working everywhere
    // (before, logging out only cleared this browser). Best effort: if the server
    // can't be reached, still sign out locally.
    try {
      await apiClient.post("logout");
    } catch {
      /* already expired or offline: nothing more to revoke */
    }
    removeToken();
    removeUser();
    navigate(SITELINKS.SIGNIN, { replace: true });
  };

  return (
    <header
      className={`fixed right-0 top-0 left-0 z-20 h-20 border-b border-hcBorder bg-white/95 backdrop-blur transition-[left] duration-200 supports-[backdrop-filter]:bg-white/85 ${
        sidebarCollapsed ? "md:left-[76px]" : "md:left-64"
      }`}
    >
      <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-7">
        {/* phones only: menu button + logo (the sidebar holds the logo on larger screens) */}
        <div className="flex items-center gap-2 md:hidden">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="admin-sidenav"
              className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
            >
              <LuMenu className="h-5 w-5" aria-hidden />
            </button>
          )}
          <Link
            to={SITELINKS.DASHBOARD}
            className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
          >
            <img src={HoneConnectLogoHeader1} alt="HoneConnect admin home" className="h-10 w-auto" />
          </Link>
        </div>
        <div className="hidden md:block" />

        <div className="flex items-center gap-1 sm:gap-3">
          <NotificationBell />

          <span aria-hidden className="hidden h-8 w-px bg-hcBorder sm:block" />

          <ProfileMenu
            name={user?.name}
            email={user?.email}
            role={user?.role}
            photo={photo}
            onLogout={handleLogout}
          />
        </div>
      </div>
    </header>
  );
};

export default HeaderAdmin;
